/**
 * Client-side catalog subscription coverage resolution.
 *
 * Subscriptions are fetched as raw rows and coverage is derived here, mirroring the
 * backend engine's rules: the subscribe engine keeps levels disjoint, a broad level
 * owns everything beneath it, and brand equality is exact — a null brand is the
 * brandless "No Brand" bucket, matching only items whose own brand is null.
 */

/** The query-param stand-in for a null scope: brand 0 is the brandless bucket, category 0 uncategorized. */
export const NO_REFERENCE_SENTINEL = 0

export type CatalogSubscriptionLevel = "catalog" | "brand" | "category" | "item"

interface SubscriptionScope {
    id: number
    level: CatalogSubscriptionLevel
    brand: number | null
    category: number | null
    catalog_item: number | null
}

interface ItemScope {
    id: number
    brand: number | null
    categories: { id: number }[]
}

/**
 * Find the subscription covering an item, or null when nothing covers it.
 *
 * Resolution mirrors the backend's effective-coverage rules: a whole-catalog
 * subscription covers anything, a brand subscription the items sharing its exact
 * brand (null matching only null), a category subscription the items filed under
 * that category that also share its brand, and an item subscription only itself.
 */
export function findCoveringSubscription<SubscriptionType extends SubscriptionScope>(
    subscriptions: SubscriptionType[],
    item: ItemScope,
): SubscriptionType | null {
    for (const subscription of subscriptions) {
        if (subscription.level === "catalog") {
            return subscription
        }
        if (subscription.level === "brand" && subscription.brand === item.brand) {
            return subscription
        }
        if (
            subscription.level === "category" &&
            subscription.brand === item.brand &&
            item.categories.some((category) => category.id === subscription.category)
        ) {
            return subscription
        }
        if (subscription.level === "item" && subscription.catalog_item === item.id) {
            return subscription
        }
    }
    return null
}

/**
 * Find the subscription pointing directly at an item, or null when its coverage is inherited.
 *
 * Only an item-level row counts: this is what a per-row unsubscribe deletes, so an
 * item covered by a broader level deliberately reports null.
 */
export function findItemSubscription<SubscriptionType extends SubscriptionScope>(
    subscriptions: SubscriptionType[],
    itemId: number,
): SubscriptionType | null {
    return (
        subscriptions.find(
            (subscription) => subscription.level === "item" && subscription.catalog_item === itemId,
        ) ?? null
    )
}

/**
 * Find the whole-catalog subscription, or null when the catalog is not subscribed as a whole.
 */
export function findCatalogSubscription<SubscriptionType extends SubscriptionScope>(
    subscriptions: SubscriptionType[],
): SubscriptionType | null {
    return subscriptions.find((subscription) => subscription.level === "catalog") ?? null
}

/**
 * Find the brand-level subscription for a brand, or null when none exists.
 *
 * Pass null for the brandless "No Brand" bucket — brand equality is exact, so the
 * bucket's subscription never matches a real brand and vice versa.
 */
export function findBrandSubscription<SubscriptionType extends SubscriptionScope>(
    subscriptions: SubscriptionType[],
    brandId: number | null,
): SubscriptionType | null {
    return (
        subscriptions.find((subscription) => subscription.level === "brand" && subscription.brand === brandId) ?? null
    )
}
